// ============================================================
// TtrTicketKeepModal — 시작/추가 티켓 keep 결정 모달
//   SoT: H:\AI\AI_Setting\보드겜돌이\티켓투라이드.md §2-3 / §4-2 KeepTickets
//   SEQ=10146 — SETUP 단계 3장 중 2~3장 keep / MAIN 단계 3장 중 1~3장 keep
//   SEQ=10214 STEP=1 (사용자 catch "도시 위치 몰라 선택 어려움") — 미니맵 SVG 추가
//                     선택/hover 시 cityA·cityB 강조 + 라인 표시, 도시명 라벨
// ============================================================
import React, { useState, useMemo } from 'react'
import { TtrPendingTicket, TtrCity } from '../store/ticket'

export interface TtrTicketKeepModalProps {
  /** 'SETUP' = DEAL_TICKETS 단계 (3장 중 최소 2 keep) / 'MAIN' = 게임 중 (최소 1 keep) */
  context:   'SETUP' | 'MAIN'
  pending:   TtrPendingTicket[]
  cities:    TtrCity[]
  onConfirm: (keepTicketIds: number[]) => void
}

const MAP_W = 360
const MAP_H = 270

const TtrTicketKeepModal: React.FC<TtrTicketKeepModalProps> = ({
  context, pending, cities, onConfirm,
}) => {
  const minKeep = context === 'SETUP' ? 2 : 1
  const maxKeep = pending.length

  // 기본 전부 keep
  const [selected, setSelected] = useState<Set<number>>(() => new Set(pending.map(t => t.ticketId)))
  // SEQ=10214 STEP=1: hover된 ticket의 도시 추가 강조 (선택 여부와 별도)
  const [hoverTicketId, setHoverTicketId] = useState<number | null>(null)

  const cityName = useMemo(() => {
    const map = new Map<number, string>()
    cities.forEach(c => map.set(c.cityId, c.name))
    return map
  }, [cities])

  const cityById = useMemo(() => {
    const map = new Map<number, TtrCity>()
    cities.forEach(c => map.set(c.cityId, c))
    return map
  }, [cities])

  // 선택된 ticket의 endpoint 도시 집합 — 미니맵 강조용
  const selectedCityIds = useMemo(() => {
    const set = new Set<number>()
    pending.forEach(t => {
      if (selected.has(t.ticketId)) {
        set.add(t.cityAId)
        set.add(t.cityBId)
      }
    })
    return set
  }, [selected, pending])

  // hover된 ticket의 endpoint 도시 집합 — 미니맵 추가 outer ring
  const hoverCityIds = useMemo(() => {
    if (hoverTicketId === null) return new Set<number>()
    const t = pending.find(tt => tt.ticketId === hoverTicketId)
    return new Set<number>(t ? [t.cityAId, t.cityBId] : [])
  }, [hoverTicketId, pending])

  function toggle(ticketId: number) {
    const next = new Set(selected)
    if (next.has(ticketId)) next.delete(ticketId)
    else                    next.add(ticketId)
    setSelected(next)
  }

  const count = selected.size
  const canConfirm = count >= minKeep && count <= maxKeep

  function handleConfirm() {
    if (!canConfirm) return
    onConfirm(Array.from(selected))
  }

  return (
    // SETUP은 cancel 불가 (전원 동시 결정), MAIN도 일단 confirm 강제
    <div className="ttr-modal-overlay" onClick={e => e.stopPropagation()}>
      <div className="ttr-modal ttr-ticket-keep-modal">
        <h2>티켓 결정 — {context === 'SETUP' ? '시작 티켓' : '추가 드로우'}</h2>
        <p>
          최소 {minKeep}장 keep · 보유 점수는 종료 시 도시 연결 여부로 ±산정
        </p>

        {/* SEQ=10214 STEP=1: 미니맵 SVG — 도시 위치 시각 보조
             선택된 ticket의 cityA·cityB는 금색 큰 원 + 라벨 + 점선 연결.
             hover된 ticket은 노란색 outer ring 추가.
             비선택 ticket의 endpoint도 옅은 회색 원으로 위치 표시. */}
        <svg
          className="ttr-ticket-keep-minimap"
          width={MAP_W}
          height={MAP_H}
          viewBox={`0 0 ${MAP_W} ${MAP_H}`}
          style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 6, border: '1px solid rgba(255,255,255,0.12)' }}
        >
          {/* selected ticket의 cityA → cityB 점선 연결 (도시 위에 그려도 무관, 도시는 layer 위) */}
          {pending.filter(t => selected.has(t.ticketId)).map(t => {
            const a = cityById.get(t.cityAId)
            const b = cityById.get(t.cityBId)
            if (!a || !b) return null
            return (
              <line
                key={`tk-line-${t.ticketId}`}
                x1={a.displayX * MAP_W} y1={a.displayY * MAP_H}
                x2={b.displayX * MAP_W} y2={b.displayY * MAP_H}
                stroke="#FFC93C" strokeWidth={1.5} strokeDasharray="5,3" opacity={0.7}
              />
            )
          })}
          {/* 모든 도시 점 + 선택/hover 강조 */}
          {cities.map(c => {
            const x = c.displayX * MAP_W
            const y = c.displayY * MAP_H
            const isSelected = selectedCityIds.has(c.cityId)
            const isHover    = hoverCityIds.has(c.cityId)
            return (
              <g key={c.cityId}>
                {isHover && (
                  <circle cx={x} cy={y} r={10} fill="none" stroke="#FFEB3B" strokeWidth={2} />
                )}
                <circle
                  cx={x} cy={y}
                  r={isSelected ? 5 : 2.5}
                  fill={isSelected ? '#FFC93C' : 'rgba(200,200,200,0.55)'}
                  stroke={isSelected ? '#7C5A00' : 'none'}
                  strokeWidth={isSelected ? 1 : 0}
                />
                {(isSelected || isHover) && (
                  <text
                    x={x + 7} y={y - 6}
                    fontSize={10}
                    fill="#fff"
                    fontWeight={isSelected ? 700 : 500}
                    style={{ paintOrder: 'stroke', stroke: 'rgba(0,0,0,0.85)', strokeWidth: 2.5 }}
                  >
                    {c.name}
                  </text>
                )}
              </g>
            )
          })}
        </svg>

        <ul className="ttr-ticket-list">
          {pending.map(t => {
            const checked = selected.has(t.ticketId)
            const aName = cityName.get(t.cityAId) ?? `#${t.cityAId}`
            const bName = cityName.get(t.cityBId) ?? `#${t.cityBId}`
            return (
              <li
                key={t.ticketId}
                className={'ttr-ticket-row' + (checked ? ' selected' : '')}
                onMouseEnter={() => setHoverTicketId(t.ticketId)}
                onMouseLeave={() => setHoverTicketId(null)}
                onTouchStart={() => setHoverTicketId(t.ticketId)}
              >
                <label>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(t.ticketId)}
                  />
                  <span className="ttr-ticket-cities">
                    {aName} ↔ {bName}
                  </span>
                  <span className="ttr-ticket-points">
                    {t.points}점
                  </span>
                  {t.isLong && <span className="ttr-ticket-long">LONG</span>}
                </label>
              </li>
            )
          })}
        </ul>

        <div className="ttr-ticket-keep-summary">
          {count} / {maxKeep} 선택 (최소 {minKeep})
        </div>

        <div className="ttr-modal-actions">
          <button onClick={handleConfirm} disabled={!canConfirm}>
            keep 확정
          </button>
        </div>
      </div>
    </div>
  )
}

export default TtrTicketKeepModal
